import { Autocomplete, Box, TextField } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useExamObservations } from 'src/features/visits/telemed/hooks/useExamObservations';
import { ExamObservationDTO } from 'utils';
import { StatelessExamCheckbox } from './StatelessExamCheckbox';

export type ExamCheckboxDropdownOptionType = { label: string; name: string };

type ControlledExamCheckboxDropdownProps = {
  checkboxLabel?: string;
  dropdownLabel?: string;
  abnormal?: boolean;
  options: ExamCheckboxDropdownOptionType[];
  dropdownTestId?: string;
  checkboxBlockTestId?: string;
};

export const ControlledExamCheckboxDropdown: FC<ControlledExamCheckboxDropdownProps> = (props) => {
  const { checkboxLabel, dropdownLabel, abnormal, options, dropdownTestId, checkboxBlockTestId } = props;
  const { t } = useTranslation();

  const { value: fields, update, isLoading } = useExamObservations(options.map((option) => option.name));

  const [selectedOption, setSelectedOption] = useState<ExamCheckboxDropdownOptionType | null>(
    fields
      .filter((field) => field.value === true)
      .map((field) => options.find((option) => option.name === field.field))[0] || null
  );
  const [booleanValue, setBooleanValue] = useState(!!selectedOption);

  const onChange = (fieldsToChange: ExamObservationDTO[]): void => {
    update(fieldsToChange);
  };

  const onBooleanChange = (newValue: boolean): void => {
    if (!newValue) {
      setSelectedOption(null);
      if (selectedOption) {
        onChange(fields.map((field) => ({ ...field, value: false })));
      }
    }
    setBooleanValue(newValue);
  };

  const onOptionChange = (newValue: ExamCheckboxDropdownOptionType | null): void => {
    setBooleanValue(!!newValue);
    setSelectedOption(newValue);
    onChange(fields.map((field) => ({ ...field, value: field.field === newValue?.name })));
  };

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Box data-testid={checkboxBlockTestId}>
        <StatelessExamCheckbox
          label={t(
            (checkboxLabel || '').startsWith('telemed.')
              ? (checkboxLabel as string)
              : `telemed.exam.labels.${checkboxLabel || ''}`,
            checkboxLabel || ''
          )}
          abnormal={abnormal}
          checked={booleanValue}
          onChange={onBooleanChange}
          disabled={isLoading}
        />
      </Box>
      <Autocomplete
        data-testid={dropdownTestId}
        disablePortal
        disabled={isLoading}
        options={options.map((option) => ({
          ...option,
          label: t(
            option.label.startsWith('telemed.') ? option.label : `telemed.exam.labels.${option.label}`,
            option.label
          ),
        }))}
        value={
          selectedOption
            ? {
                ...selectedOption,
                label: t(
                  selectedOption.label.startsWith('telemed.')
                    ? selectedOption.label
                    : `telemed.exam.labels.${selectedOption.label}`,
                  selectedOption.label
                ),
              }
            : null
        }
        isOptionEqualToValue={(option, value) => option.name === value.name}
        onChange={(_e, newValue) => onOptionChange(newValue)}
        fullWidth
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t(
              (dropdownLabel || '').startsWith('telemed.')
                ? (dropdownLabel as string)
                : `telemed.exam.labels.${dropdownLabel || ''}`,
              dropdownLabel || ''
            )}
          />
        )}
      />
    </Box>
  );
};
